import type { ControlCompanyListItem } from "@/models/company.model";
import { NO_DATA_ERROR } from "@/shared/constants/errors";
import { useCompanyRepository } from "@/shared/contexts/repository-context";
import { useHasCompanyPermission } from "@/shared/hooks/use-has-permission";
import { getGetCompanyListQueryKey } from "@/spz-api/companies/companies";
import { useGetMe } from "@/spz-api/user/user";
import { CompanyPermission } from "@spz/shared-domain";
import { useQuery } from "@tanstack/react-query";
import { useQueryState } from "nuqs";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMainControlFiltersStore } from "../stores/main-control-filters.store";

export function useControlListViewModel() {
  const { t } = useTranslation();
  const companyRepository = useCompanyRepository();

  const { methods, selectedAnalysisId, selectedCompanyId } =
    useMainControlFiltersStore();

  const [analysisId, setAnalysisId] = useQueryState("analysis", {
    defaultValue: "",
  });

  const { data: user } = useGetMe();

  const hasExportReportPermission = useHasCompanyPermission(
    CompanyPermission.AnalysisReportExport,
    user,
    selectedCompanyId ?? "",
  );

  const params = {
    includePeriodsWithAnalysis: true,
  };
  useQuery<ControlCompanyListItem[]>({
    queryKey: getGetCompanyListQueryKey(params),
    queryFn: async () => {
      const companies = await companyRepository.getControlCompanies(params);
      if (companies.length === 0) {
        throw new Error(NO_DATA_ERROR);
      }
      methods.onCompaniesLoaded(companies, analysisId);
      return companies;
    },
  });

  useEffect(() => {
    if (selectedAnalysisId) {
      setAnalysisId(selectedAnalysisId);
    }
  }, [setAnalysisId, selectedAnalysisId]);

  return {
    t,
    selectedAnalysisId,
    hasExportReportPermission,
  };
}
